import {
  faArrowLeft,
  faBook,
  faCalendar,
  faCreditCard,
  faIndianRupeeSign,
  faLayerGroup,
  faPlus,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  GroupPaymentActions,
  GroupPaymentProof,
  GroupPaymentProofModal,
  GroupPaymentRow,
  GroupPaymentStatusBadge,
} from "components/GroupPayment/GroupPayment.helpers";
import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { ExamGroupService } from "Services/examGrouo";
import { ExamServices } from "Services/examServices";

interface ExamGroup {
  _id: string;
  name: string;
  fees?: number;
  isManualPayment?: boolean;
  paymentLink?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ExamItem {
  _id: string;
  examName: string;
}

export default function ExamGroupDetails() {
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [group, setGroup] = useState<ExamGroup | null>(null);
  const [exams, setExams] = useState<ExamItem[]>([]);
  const [allExams, setAllExams] = useState<ExamItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedExam, setSelectedExam] = useState<{ value: string; label: string } | null>(null);
  const [adding, setAdding] = useState(false);

  const [payments, setPayments] = useState<GroupPaymentRow[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [viewingProof, setViewingProof] = useState<GroupPaymentRow | null>(null);

  const getGroupDetails = async () => {
    try {
      const res = await ExamGroupService.getExamGroupById(groupId);
      if (res?.status === 200) {
        setGroup(res.data.data.data || null);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch exam group");
    }
  };

  const getExamsInGroup = async () => {
    setLoading(true);
    try {
      const res = await ExamGroupService.getAllExamsInGroup(groupId);
      if (res?.status === 200) {
        setExams(res.data.data.data || res.data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAllExams = async () => {
    try {
      const res = await ExamServices.getAllExamsLean();
      if (res?.status === 200) {
        setAllExams(res.data.data?.examData || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getGroupPayments = async () => {
    setPaymentsLoading(true);
    try {
      const res = await ExamGroupService.getGroupPayments(groupId);
      if (res?.status === 200) {
        setPayments(res.data.data.data || res.data.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch payment requests");
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (!groupId) return;
    getGroupDetails();
    getExamsInGroup();
    getAllExams();
    getGroupPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const handleApprovePayment = async (paymentId: string) => {
    setActioningId(paymentId);
    try {
      const res = await ExamGroupService.approveGroupPayment(paymentId);
      if (res?.status === 200) {
        toast.success("Payment approved");
        getGroupPayments();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to approve payment");
    } finally {
      setActioningId(null);
    }
  };

  const handleRejectPayment = async (paymentId: string) => {
    setActioningId(paymentId);
    try {
      const res = await ExamGroupService.rejectGroupPayment(paymentId);
      if (res?.status === 200) {
        toast.success("Payment rejected");
        getGroupPayments();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to reject payment");
    } finally {
      setActioningId(null);
    }
  };

  const availableExams = allExams
    .filter((e) => !exams.some((added) => added._id === e._id))
    .map((e) => ({ value: e._id, label: e.examName }));

  const handleAddExam = async () => {
    if (!selectedExam) {
      toast.error("Please select an exam");
      return;
    }
    setAdding(true);
    try {
      const res = await ExamGroupService.addExamToGroup(
        { examId: selectedExam.value },
        groupId
      );
      if (res?.status === 200 || res?.status === 201) {
        toast.success("Exam added to group");
        setSelectedExam(null);
        getExamsInGroup();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add exam");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveExam = async (examId: string) => {
    try {
      const res = await ExamGroupService.removeExamFromGroup(examId);
      if (res?.status === 200) {
        toast.success("Exam removed from group");
        getExamsInGroup();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove exam");
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <span
          className="xrg-back-btn"
          role="button"
          onClick={() => navigate(-1)}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </span>
        <div>
          <h5 className="mb-0 fw-bold">{group?.name || "Exam Group"}</h5>
          <span className="text-muted fs-12">Exam group details</span>
        </div>
      </div>

      {/* Info / stat tiles */}
      <Card className="xrg-card shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faLayerGroup} />
                </span>
                <div>
                  <div className="xrg-stat-label">Name</div>
                  <div className="xrg-stat-value">{group?.name || "-"}</div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faCalendar} />
                </span>
                <div>
                  <div className="xrg-stat-label">Created</div>
                  <div className="xrg-stat-value">
                    {group?.createdAt
                      ? new Date(group.createdAt).toLocaleDateString()
                      : "-"}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faIndianRupeeSign} />
                </span>
                <div>
                  <div className="xrg-stat-label">Fees</div>
                  <div className="xrg-stat-value">
                    {group?.fees
                      ? `₹${group.fees.toLocaleString('en-IN')}`
                      : 'Free'}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faBook} />
                </span>
                <div>
                  <div className="xrg-stat-label">Total Exams</div>
                  <div className="xrg-stat-value">{exams.length}</div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faCreditCard} />
                </span>
                <div>
                  <div className="xrg-stat-label">Payment Mode</div>
                  <div className="xrg-stat-value">
                    {group?.isManualPayment ? "Manual" : "Online"}
                  </div>
                </div>
              </div>
            </Col>
            {group?.isManualPayment && (
              <Col md={9}>
                <div className="xrg-stat">
                  <span className="xrg-stat-icon">
                    <FontAwesomeIcon icon={faIndianRupeeSign} />
                  </span>
                  <div className="text-truncate">
                    <div className="xrg-stat-label">Payment Link</div>
                    <div className="xrg-stat-value text-truncate">
                      {group?.paymentLink ? (
                        <a
                          href={group.paymentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {group.paymentLink}
                        </a>
                      ) : (
                        <span className="text-danger">Not configured</span>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Exams table */}
      <Card className="xrg-card xrg-table-card shadow-sm">
        <Card.Header className="xrg-card-header">
          <Row className="g-2 align-items-center">
            <Col>
              <span className="fw-bold">Exams in this group</span>
            </Col>
            <Col md={5}>
              <Select
                options={availableExams}
                isClearable
                placeholder="Select an exam to add..."
                value={selectedExam}
                onChange={(e: any) => setSelectedExam(e)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                maxMenuHeight={320}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </Col>
            <Col xs="auto">
              <Button size="sm" onClick={handleAddExam} disabled={adding}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {adding ? "Adding..." : "Add"}
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="xrg-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: 64 }}>#</th>
                <th>Exam Name</th>
                <th className="text-center" style={{ width: 90 }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading exams...
                  </td>
                </tr>
              ) : exams?.length > 0 ? (
                exams.map((exam, index) => (
                  <tr key={exam._id}>
                    <td className="text-muted">{index + 1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="xrg-table-icon">
                          <FontAwesomeIcon icon={faBook} />
                        </span>
                        <span className="fw-semibold">
                          {exam.examName || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="text-center">
                      <FontAwesomeIcon
                        icon={faTrash}
                        role="button"
                        title="Remove from group"
                        className="text-danger cursor-pointer px-2"
                        onClick={() => handleRemoveExam(exam._id)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center py-5">
                    <span className="xrg-table-icon mx-auto mb-3 d-flex">
                      <FontAwesomeIcon icon={faBook} />
                    </span>
                    <h6 className="fw-bold mb-1">No exams in this group</h6>
                    <p className="text-muted fs-12 mb-0">
                      Use the selector above to add an exam.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Manual payment requests */}
      {group?.isManualPayment && (
        <Card className="xrg-card xrg-table-card shadow-sm mt-4">
          <Card.Header className="xrg-card-header">
            <Row className="g-2 align-items-center">
              <Col>
                <span className="fw-bold">Manual Payment Requests</span>
                <div className="text-muted fs-12">
                  Approving a request gives the candidate access to enroll in this group's exams.
                </div>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover responsive className="xrg-table mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>#</th>
                  <th>Candidate</th>
                  <th>Reg. No.</th>
                  <th>Email</th>
                  <th>Requested On</th>
                  <th>Proof</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: 200 }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {paymentsLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading payment requests...
                    </td>
                  </tr>
                ) : payments?.length > 0 ? (
                  payments.map((payment, index) => (
                    <tr key={payment._id}>
                      <td className="text-muted">{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="xrg-table-icon">
                            <FontAwesomeIcon icon={faUser} />
                          </span>
                          <span className="fw-semibold">
                            {`${payment.student?.name || ""} ${payment.student?.last_name || ""}`.trim() || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="fs-12">{payment.student?.registrationNumber || "-"}</td>
                      <td className="fs-12">{payment.student?.email || "-"}</td>
                      <td className="fs-12">
                        {payment.requestedAt
                          ? new Date(payment.requestedAt).toLocaleString()
                          : "-"}
                      </td>
                      <td>
                        <GroupPaymentProof payment={payment} onView={setViewingProof} />
                      </td>
                      <td>
                        <GroupPaymentStatusBadge payment={payment} />
                      </td>
                      <td className="text-center">
                        <GroupPaymentActions
                          payment={payment}
                          busy={actioningId === payment._id}
                          onApprove={handleApprovePayment}
                          onReject={handleRejectPayment}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <span className="xrg-table-icon mx-auto mb-3 d-flex">
                        <FontAwesomeIcon icon={faCreditCard} />
                      </span>
                      <h6 className="fw-bold mb-1">No payment requests yet</h6>
                      <p className="text-muted fs-12 mb-0">
                        Requests appear here once candidates confirm they have paid.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <GroupPaymentProofModal
        payment={viewingProof}
        onHide={() => setViewingProof(null)}
      />
    </Container>
  );
}
